import api, { methods } from "@api";
import type { QueryKey } from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import type { Product } from "@type/product";

interface StockResponse {
  status: string;
  stock_status: Record<number, number | null>; // Simple map: product_id -> effective stock
  timestamp: number;
}

// Elke 30 s: vaker levert niets op (voorraad verandert per bestelling) en elke
// wijziging laat de productlijsten opnieuw rekenen.
const STOCK_POLL_INTERVAL = 30 * 1000;

/**
 * Hook to keep product stock synchronized using React Query
 * - Polls every 30 seconds when app is active
 * - Refreshes on window focus (web) and app focus (mobile, via focusManager)
 * - Updates the global products query data with latest stock values
 * - Only fetches stock for products that have voorraad management
 */
export function useStockRefresh(enabled: boolean, products: Product[] | undefined, productsQueryKey: QueryKey) {
  const queryClient = useQueryClient();
  const productIds = useMemo(
    () => (products || []).filter((p) => p.voorraad !== null && p.voorraad !== undefined).map((p) => p.id),
    [products],
  );

  // Use React Query for stock fetching with automatic refetch features
  const { data: stockData } = useQuery({
    queryKey: ["stock-status", productIds],
    queryFn: async () => {
      if (productIds.length === 0) {
        return null;
      }

      const response = await api<StockResponse>({
        path: "/stock/status",
        method: methods.POST,
        body: { product_ids: productIds },
      });

      // Validate response
      if (!response || typeof response !== "object") {
        console.log("Invalid stock status response");
        return null;
      }

      return response;
    },
    enabled: enabled && productIds.length > 0,
    refetchInterval: STOCK_POLL_INTERVAL,
    refetchIntervalInBackground: false, // Don't poll in background
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnReconnect: true, // Refetch when reconnecting
    staleTime: STOCK_POLL_INTERVAL / 2,
    gcTime: STOCK_POLL_INTERVAL,
  });

  // Update global products query data with new stock values
  useEffect(() => {
    if (!stockData?.stock_status) return;

    const oldProducts = queryClient.getQueryData<Product[]>(productsQueryKey);
    if (!oldProducts) return;

    // Niets veranderd: cache (en daarmee de lijsten) ongemoeid laten.
    const newProducts = applyStock(oldProducts, stockData.stock_status);
    if (newProducts !== oldProducts) {
      queryClient.setQueryData<Product[]>(productsQueryKey, newProducts);
    }
  }, [stockData, queryClient, productsQueryKey]);
}

// Nieuwe array alleen als minstens één voorraad wijzigt; ongewijzigde
// producten houden hun object (identiteit), zodat memo's blijven werken.
export function applyStock(products: Product[], stockMap: StockResponse["stock_status"]): Product[] {
  let changed = false;
  const next = products.map((product) => {
    const newStock = stockMap[product.id];
    if (newStock === undefined || newStock === product.voorraad) {
      return product;
    }
    changed = true;
    return { ...product, voorraad: newStock };
  });
  return changed ? next : products;
}
